import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import { fancyFormatDate, handleAuthError, newFormatDate } from "../../utils";
import {
    Badge,
    Button,
    Col,
    Container,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Tooltip,
} from "reactstrap";
import withAuth from "../withAuth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import { useConfirm } from "../../contexts/ConfirmContext";
import Moment from "react-moment";
import PaginationControls from "../../components/Common/PaginationControls";
import ActionsDropdown from "../../components/Common/ActionsDropdown";
import StatusBadge from "../../components/Common/StatusBadge";

const ManageProgram = () => {
    const { showConfirm } = useConfirm();
    const [cohort, setCohort] = useState([]);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [newCohort, setNewCohort] = useState({ title: "", description: "", startDate: "", endDate: "" });
    const [editCohort, setEditCohort] = useState({ id: "", title: "", description: "", startDate: "", endDate: "" });
    const [loading, setLoading] = useState(true);
    const [toolTipValue, setToolTipValue] = useState("");
    const [startTooltipOpen, setStartTooltipOpen] = useState(false);
    const [statusTooltipOpen, setStatusTooltipOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [lessonStatusModalCohort, setLessonStatusModalCohort] = useState(null);
    const [regStatusModalCohort, setRegStatusModalCohort] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        document.title = "Manage Cohort | RYD Admin";
        fetchCohort().then(r => null);
    }, [currentPage, pageSize, searchQuery]);

    const fetchCohort = async () => {
        try {
            setLoading(true);
            const limit = showAll ? 10000 : pageSize;
            const params = {
                page: currentPage,
                limit: limit,
                ...(searchQuery && { search: searchQuery })
            };
            const response = await axios.get(`${baseUrl}/admin/cohort/all`, { params });
            const responseData = response.data.data;
            const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
            const paginationData = responseData?.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalItems: Array.isArray(data) ? data.length : 0,
                itemsPerPage: limit,
                hasNextPage: false,
                hasPreviousPage: false
            };
            setCohort(Array.isArray(data) ? data : []);
            setPagination(paginationData);
            setLoading(false);
        } catch (error) {
            handleAuthError(error);
            setLoading(false);
            console.error("Error:", error);
            toast.error("Failed to load cohorts. List may be outdated.");
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size) => {
        if (size === 'all') {
            setShowAll(true);
            setPageSize(10000);
        } else {
            setShowAll(false);
            setPageSize(parseInt(size));
            setCurrentPage(1);
        }
    };

    const goToNextPage = () => {
        if (pagination.hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (pagination.hasPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    // Add new cohort
    const addNewCohort = async () => {
        try {
            if (newCohort.description.length > 0 || newCohort.startDate.length > 0 || newCohort.endDate.length > 0 || newCohort.title.length > 0) {
                const d = await axios.post(`${baseUrl}/admin/cohort/create`, newCohort);
                toast.success("New cohort created successfully");
                setModal(false);
                setNewCohort({ title: "", description: "", startDate: "", endDate: "" }); // Reset form
                if (d.data.status) {
                    fetchCohort()
                }
            } else {
                toast.error("Please complete cohort form and try again...")
            }
        } catch (ex) {
            toast.error("Reload page and try again !")
        }
    }

    // Edit cohort function
    const updateCohort = async () => {
        try {
            if (editCohort.description.length > 0 || editCohort.startDate.length > 0 || editCohort.endDate.length > 0 || editCohort.title.length > 0) {
                toast.warn("Updating, please wait...")
                const response = await axios.post(`${baseUrl}/admin/cohort/update/${editCohort.id}`, {
                    title: editCohort.title,
                    description: editCohort.description,
                    startDate: editCohort.startDate,
                    endDate: editCohort.endDate
                });
                toast.success("Cohort updated successfully");
                setEditModal(false);
                setEditCohort({ id: "", title: "", description: "", startDate: "", endDate: "" }); // Reset form
                if (response.data.status) {
                    fetchCohort()
                }
            } else {
                toast.error("Please complete cohort form and try again...")
            }
        } catch (ex) {
            handleAuthError(ex);
            toast.error("Failed to update cohort!");
        }
    }

    // Open edit modal with cohort data
    const openEditModal = (cohortData) => {
        setEditCohort({
            id: cohortData.id,
            title: cohortData.title,
            description: cohortData.description,
            startDate: cohortData.startDate ? cohortData.startDate.split('T')[0] : "", // Format date for input
            endDate: cohortData.endDate ? cohortData.endDate.split('T')[0] : ""
        });
        setEditModal(true);
    };

    // Toggle cohort to "Not Started" status
    const resetCohortToNotStarted = async (id) => {
        try {
            const confirmed = await showConfirm({ title: "Reset cohort", message: "Are you sure you want to reset this cohort to 'Not Started'?", variant: "danger" });
            if (confirmed) {
                toast.warn("Resetting cohort, please wait...");
                const response = await axios.post(`${baseUrl}/admin/cohort/update/${id}`, {
                    notStarted: true,
                    isStarted: false
                });
                if (response.data.status) {
                    toast.success("Cohort reset to 'Not Started' successfully");
                    fetchCohort()
                }
            }
        } catch (ex) {
            handleAuthError(ex);
            toast.error("Unable to reset cohort at the moment...");
        }
    }

    const resetCohortToStarted = async (id) => {
        try {
            const confirmed = await showConfirm({ title: "Reset cohort", message: "Are you sure you want to reset this cohort to 'Not Started'?", variant: "danger" });
            if (confirmed) {
                toast.warn("Resetting cohort, please wait...");
                const response = await axios.post(`${baseUrl}/admin/cohort/update/${id}`, {
                    notStarted: false,
                    isStarted: true
                });
                if (response.data.status) {
                    toast.success("Cohort reset to 'Not Started' successfully");
                    fetchCohort()
                }
            }
        } catch (ex) {
            handleAuthError(ex);
            toast.error("Unable to reset cohort at the moment...");
        }
    }

    // Existing functions remain the same
    const updateRecords = async (id, t, d) => {
        try {
            if (t === "d") {
                const confirmed = await showConfirm({ title: "Remove cohort", message: "Are you sure to remove this cohort?", variant: "danger" });
                if (confirmed) {
                    toast.warn("Deleting, please wait !")
                    const response = await axios.get(`${baseUrl}/admin/cohort/remove/${id}`);
                    if (response.data.status) {
                        fetchCohort()
                    }
                }
            }
            if (t === "u") {
                const confirmedToggle = await showConfirm({ title: "Toggle state", message: "Are you sure to toggle the current state?", variant: "primary" });
                if (confirmedToggle) {
                    toast.warn("Updating, please wait...")
                    const response = await axios.post(`${baseUrl}/admin/cohort/update/${id}`, d);
                    if (response.data.status) {
                        fetchCohort()
                    }
                }
            }
        } catch (ex) {
            toast.error("Unable to perform this operations at the moment...")
        }
    }

    const applyLessonStatus = async (id, start) => {
        try {
            toast.warn("Updating, please wait...");
            // Start => isStarted true; Stop => isStarted false, notStarted false (so Manage Lesson shows "Finished" not "Not started")
            const response = await axios.post(`${baseUrl}/admin/cohort/update/${id}`, {
                isStarted: !!start,
                notStarted: false
            });
            if (response.data.status) {
                toast.success(start ? "Lesson status started" : "Lesson status stopped");
                setLessonStatusModalCohort(null);
                fetchCohort();
            }
        } catch (ex) {
            handleAuthError(ex);
            toast.error("Unable to update lesson status");
        } finally {
            setActionLoadingId(null);
        }
    };

    const applyRegStatus = async (id, open) => {
        try {
            toast.warn("Updating, please wait...");
            const response = await axios.post(`${baseUrl}/admin/cohort/update/${id}`, { status: !!open });
            if (response.data.status) {
                toast.success(open ? "Registration opened" : "Registration closed");
                setRegStatusModalCohort(null);
                fetchCohort();
            }
        } catch (ex) {
            handleAuthError(ex);
            toast.error("Unable to update registration status");
        } finally {
            setActionLoadingId(null);
        }
    };

    const completeCohort = async (id) => {
        try {
            const confirmed = await showConfirm({ title: "Complete all Program", message: "Are you sure you want to complete all programs in this cohort?", variant: "danger" });
            if (confirmed) {
                toast.warn("Completing, please wait !")
                const response = await axios.put(`${baseUrl}/admin/cohort/complete/${id}`);
                if (response.data.status) {
                    setTimeout(() => fetchCohort(), 2000)
                }
            }
        } catch (ex) {
            toast.error("Unable to perform this operations at the moment...")
        }
    }

    const unCompleteCohort = async (id) => {
        try {
            const confirmed = await showConfirm({ title: "Open all Program", message: "Are you sure you want to open all programs in this cohort?", variant: "danger" });
            if (confirmed) {
                toast.warn("Un Completing, please wait !")
                const response = await axios.put(`${baseUrl}/admin/cohort/uncomplete/${id}`);
                if (response.data.status) {
                    setTimeout(() => fetchCohort(), 2000)
                }
            }
        } catch (ex) {
            toast.error("Unable to perform this operations at the moment...")
        }
    }

    function countPaidPrograms(programs) {
        return programs.filter(program => program.isPaid).length;
    }

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Cohort" />
                    <Row>
                        <Col lg="12">
                            <Row className="align-items-center">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <h5 className="card-title">
                                            Manage & Create Cohorts
                                            <span className="text-muted fw-normal ms-2"></span>
                                        </h5>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                        <div>
                                            <Button onClick={() => setModal(true)} className={'btn btn-danger'}
                                                type="text">Create New Cohort</Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xl="12">
                                    <div className="table-responsive">
                                    <table className="table align-middle">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Title</th>
                                                <th>Descriptions</th>
                                                <th>Enrolled</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>When</th>
                                                <th>Lesson Status</th>
                                                <th>Reg. Status</th>
                                                <th>Revoke Access</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(cohort && cohort.length > 0) ?
                                                cohort.map((d, i) => {
                                                    const globalIndex = showAll 
                                                        ? i 
                                                        : (pagination.currentPage - 1) * pagination.itemsPerPage + i;
                                                    return <tr key={d.id || i} style={{ backgroundColor: (d.status && d.isStarted) ? "#effff2" : (d.isStarted) ? "#FFFAD1FF" : (d.status) ? "#fff1f1" : "#fff" }}>
                                                        <td>{globalIndex + 1}</td>
                                                        <td>{d.title}</td>
                                                        <td style={{ width: 200 }}>{d.description}</td>
                                                        <td>{countPaidPrograms(d?.programs)}</td>
                                                        <td>{fancyFormatDate(d.startDate)}</td>
                                                        <td>{fancyFormatDate(d.endDate)}</td>
                                                        <td><Moment date={d.startDate} fromNow={true} /></td>
                                                        <td>{d.notStarted ? <StatusBadge variant="danger">
                                                            {"Not started"}
                                                        </StatusBadge> : d.isStarted ? <span className={'blink'}>Ongoing</span> : <StatusBadge variant="success">
                                                            {/* Stop sets isStarted=false & notStarted=false; new cohorts use notStarted=true until Start */}
                                                            {"Finished"}
                                                        </StatusBadge>}</td>
                                                        <td>{d.status ? "Open" : "Closed"}</td>
                                                        <td>{d.access === false ? "Completed" : "Not Completed"}</td>
                                                        <td>
                                                            <ActionsDropdown
                                                                loading={actionLoadingId === d.id}
                                                                toggleTitle="Cohort actions"
                                                                items={[
                                                                    { label: "Edit", icon: "mdi-pencil", onClick: () => openEditModal(d), variant: "primary" },
                                                                    { label: "Manage Lesson Status", icon: "mdi-human-child", onClick: () => setLessonStatusModalCohort(d) },
                                                                    { label: "Manage Reg. Status", icon: "mdi-exit-run", onClick: () => setRegStatusModalCohort(d) },
                                                                    { label: d.access !== false ? "Revoke class access" : "Allow class access", icon: "mdi-account-key", onClick: () => { const newAccess = d.access !== false ? false : true; const title = newAccess ? "Allow class access?" : "Revoke class access?"; const msg = newAccess ? "Students in this cohort will be able to use 'Go to class' and the card will show as Active until you revoke access." : "Students in this cohort will no longer see 'Go to class' and the card will show as Inactive."; showConfirm({ title, message: msg, variant: "primary" }).then(async (confirmed) => { if (confirmed) { setActionLoadingId(d.id); try { const response = await axios.post(`${baseUrl}/admin/cohort/update/${d.id}`, { access: newAccess }); if (response.data.status) { toast.success(newAccess ? "Class access allowed" : "Class access revoked"); fetchCohort(); } } catch (ex) { handleAuthError(ex); toast.error("Unable to update access"); } finally { setActionLoadingId(null); } } }); }, variant: d.access !== false ? "success" : "secondary" },
                                                                    { divider: true },
                                                                    ...((d.isStarted || d?.programs?.length > 0) ? [{ label: "Reset to Not Started", icon: "mdi-refresh", onClick: async () => { setActionLoadingId(d.id); try { await resetCohortToNotStarted(d.id); } finally { setActionLoadingId(null); } }, variant: "warning" }] : [{ label: "Reset to Started", icon: "mdi-refresh-circle", onClick: async () => { setActionLoadingId(d.id); try { await resetCohortToStarted(d.id); } finally { setActionLoadingId(null); } }, variant: "warning" }]),
                                                                    { label: "Delete", icon: "mdi-delete-forever", onClick: async () => { if (d?.programs?.length > 0) toast.warn("Unable to delete this cohort. Currently enrolled"); else { setActionLoadingId(d.id); try { await updateRecords(d.id, "d", d); } finally { setActionLoadingId(null); } } }, variant: "danger" },
                                                                    { divider: true },
                                                                    ...(d.isStarted ? [{ label: "Complete all Program", icon: "mdi-check", onClick: async () => { setActionLoadingId(d.id); try { await completeCohort(d.id); } finally { setActionLoadingId(null); } }, variant: "success" }] : [{ label: "Open all Program", icon: "mdi-cancel", onClick: async () => { setActionLoadingId(d.id); try { await unCompleteCohort(d.id); } finally { setActionLoadingId(null); } }, variant: "danger" }]),
                                                                ]}
                                                            />
                                                        </td>
                                                    </tr>
                                                })
                                                : null
                                            }
                                        </tbody>
                                    </table>
                                    </div>
                                    {cohort && cohort.length > 0 && (
                                        <PaginationControls
                                            currentPage={pagination.currentPage}
                                            totalPages={pagination.totalPages}
                                            totalItems={pagination.totalItems}
                                            pageSize={pagination.itemsPerPage}
                                            showAll={showAll}
                                            handlePageChange={handlePageChange}
                                            handlePageSizeChange={handlePageSizeChange}
                                            goToNextPage={goToNextPage}
                                            goToPreviousPage={goToPreviousPage}
                                            canPreviousPage={pagination.hasPreviousPage}
                                            canNextPage={pagination.hasNextPage}
                                            paginatedDataLength={cohort.length}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Manage Lesson Status Modal */}
            <Modal isOpen={!!lessonStatusModalCohort} toggle={() => setLessonStatusModalCohort(null)} centered>
                <ModalHeader toggle={() => setLessonStatusModalCohort(null)}>Manage Lesson Status</ModalHeader>
                <ModalBody>
                    {lessonStatusModalCohort && (
                        <>
                            <p className="text-muted mb-3">
                                Choose Start or Stop for cohort: <strong>{lessonStatusModalCohort.title}</strong>
                            </p>
                            <div className="d-flex gap-2 justify-content-end">
                                <Button
                                    color="success"
                                    onClick={() => {
                                        setActionLoadingId(lessonStatusModalCohort.id);
                                        applyLessonStatus(lessonStatusModalCohort.id, true);
                                    }}
                                    disabled={actionLoadingId === lessonStatusModalCohort.id}
                                >
                                    Start
                                </Button>
                                <Button
                                    color="danger"
                                    onClick={() => {
                                        setActionLoadingId(lessonStatusModalCohort.id);
                                        applyLessonStatus(lessonStatusModalCohort.id, false);
                                    }}
                                    disabled={actionLoadingId === lessonStatusModalCohort.id}
                                >
                                    Stop
                                </Button>
                                <Button color="light" onClick={() => setLessonStatusModalCohort(null)}>Cancel</Button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>

            {/* Manage Reg. Status Modal */}
            <Modal isOpen={!!regStatusModalCohort} toggle={() => setRegStatusModalCohort(null)} centered>
                <ModalHeader toggle={() => setRegStatusModalCohort(null)}>Manage Reg. Status</ModalHeader>
                <ModalBody>
                    {regStatusModalCohort && (
                        <>
                            <p className="text-muted mb-3">
                                Choose Open or Close for cohort: <strong>{regStatusModalCohort.title}</strong>
                            </p>
                            <div className="d-flex gap-2 justify-content-end">
                                <Button
                                    color="success"
                                    onClick={() => {
                                        setActionLoadingId(regStatusModalCohort.id);
                                        applyRegStatus(regStatusModalCohort.id, true);
                                    }}
                                    disabled={actionLoadingId === regStatusModalCohort.id}
                                >
                                    Open
                                </Button>
                                <Button
                                    color="danger"
                                    onClick={() => {
                                        setActionLoadingId(regStatusModalCohort.id);
                                        applyRegStatus(regStatusModalCohort.id, false);
                                    }}
                                    disabled={actionLoadingId === regStatusModalCohort.id}
                                >
                                    Close
                                </Button>
                                <Button color="light" onClick={() => setRegStatusModalCohort(null)}>Cancel</Button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>

            {/* Create New Cohort Modal */}
            <Modal isOpen={modal} toggle={() => setModal(false)}>
                <ModalHeader toggle={() => setModal(false)}>Create New Cohort</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col xs={12}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <Label>Cohort Title</Label>
                                        <Input
                                            value={newCohort.title}
                                            onChange={e => setNewCohort({ ...newCohort, title: e.target.value })}
                                            type="text"
                                            name="title"
                                            placeholder={"Title"}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="mb-3">
                                        <Label>Description</Label>
                                        <Input
                                            value={newCohort.description}
                                            onChange={e => setNewCohort({ ...newCohort, description: e.target.value })}
                                            type="text"
                                            placeholder={"Describe your next cohort..."}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <Label>Start Date</Label>
                                        <Input
                                            value={newCohort.startDate}
                                            onChange={e => setNewCohort({ ...newCohort, startDate: e.target.value })}
                                            type="date"
                                            placeholder={"Choose date"}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <Label>End Date</Label>
                                        <Input
                                            value={newCohort.endDate}
                                            onChange={e => setNewCohort({ ...newCohort, endDate: e.target.value })}
                                            type="date"
                                            placeholder={"Choose date"}>
                                        </Input>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => {
                                        setModal(false);
                                        setNewCohort({ title: "", description: "", startDate: "", endDate: "" });
                                    }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={addNewCohort}
                                    type="submit"
                                    className="btn btn-primary save-user">
                                    Create Cohort
                                </button>
                            </div>
                        </Col>
                    </Row>
                </ModalBody>
            </Modal>

            {/* Edit Cohort Modal */}
            <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
                <ModalHeader toggle={() => setEditModal(false)}>Edit Cohort</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col xs={12}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <Label>Cohort Title</Label>
                                        <Input
                                            value={editCohort.title}
                                            onChange={e => setEditCohort({ ...editCohort, title: e.target.value })}
                                            type="text"
                                            name="title"
                                            placeholder={"Title"}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="mb-3">
                                        <Label>Description</Label>
                                        <Input
                                            value={editCohort.description}
                                            onChange={e => setEditCohort({ ...editCohort, description: e.target.value })}
                                            type="text"
                                            placeholder={"Describe your cohort..."}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <Label>Start Date</Label>
                                        <Input
                                            value={editCohort.startDate}
                                            onChange={e => setEditCohort({ ...editCohort, startDate: e.target.value })}
                                            type="date"
                                            placeholder={"Choose date"}>
                                        </Input>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <Label>End Date</Label>
                                        <Input
                                            value={editCohort.endDate}
                                            onChange={e => setEditCohort({ ...editCohort, endDate: e.target.value })}
                                            type="date"
                                            placeholder={"Choose date"}>
                                        </Input>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => {
                                        setEditModal(false);
                                        setEditCohort({ id: "", title: "", description: "", startDate: "", endDate: "" });
                                    }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={updateCohort}
                                    type="submit"
                                    className="btn btn-primary save-user">
                                    Update Cohort
                                </button>
                            </div>
                        </Col>
                    </Row>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default withAuth(ManageProgram);